import { useEffect, useRef } from "react";
import { useDispatch, useSelector, useStore } from "react-redux";
import { State } from "../../app/store";
import { refreshUserToken } from "../../app/store/auth/authActions";
import { UserToken } from "../../app/models/UserToken";
import useFacility from "./useFacility";

const REFRESH_BEFORE_EXPIRY_MS = 5 * 60 * 1000; // odśwież 5 minut przed wygaśnięciem
const MIN_REFRESH_INTERVAL_MS = 30 * 1000; // minimalny odstęp między odświeżeniami (30s)

export default function useAutoRefreshSession() {
  const dispatch = useDispatch();
  const store = useStore<State>();
  const facilityIds = useFacility();
  const userToken = useSelector((state: State) => state.auth.userToken);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const userTokenRef = useRef<UserToken>(userToken);

  useEffect(() => {
    userTokenRef.current = userToken;
  }, [userToken]);

  const canAutoRefresh = (token: UserToken) => !!(token?.rememberMe && token?.refreshToken && token?.tokenValidTo);

  const wasJustRefreshed = (token: UserToken) =>
    !!(token?.lastRefreshedAt && Date.now() - token.lastRefreshedAt < MIN_REFRESH_INTERVAL_MS);

  const triggerRefresh = (token: UserToken) => {
    if (!canAutoRefresh(token) || !token.refreshToken) return;
    if (wasJustRefreshed(token)) return;
    if (store.getState().auth.loading) return;
    dispatch(refreshUserToken({ objectIds: facilityIds, refreshToken: token.refreshToken }));
  };

  const scheduleRefresh = (token: UserToken) => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }

    if (!canAutoRefresh(token)) return;

    const expiryTime = new Date(token.tokenValidTo).getTime();
    const timeUntilRefresh = expiryTime - Date.now() - REFRESH_BEFORE_EXPIRY_MS;

    if (timeUntilRefresh <= 0) {
      triggerRefresh(token);
    } else {
      timerRef.current = setTimeout(() => triggerRefresh(userTokenRef.current), timeUntilRefresh);
    }
  };

  useEffect(() => {
    scheduleRefresh(userToken);
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userToken]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        scheduleRefresh(userTokenRef.current);
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
